import { useEffect, useState, type FormEvent } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { PageLoader } from '@/components/ui/Spinner';
import { FormErrorAlert } from '@/features/auth/FormErrorAlert';
import { getErrorMessage } from '@/lib/api-error';
import { EMPTY_FORM_ERRORS, fieldError, formErrorsFromApiError } from '@/lib/form-errors';
import type { SendTemplateResult, TemplateCatalog, WhatsappTemplate } from '@/types/templates';
import {
  campoEtiqueta,
  describeEncabezado,
  describeIdentidad,
  summarizeSend,
  templateStatusMeta,
  variablesDelCuerpo,
} from './templates-format';
import {
  SEND_TEST_FORM_FIELDS,
  emptySendTestForm,
  sendTestPayload,
  valoresEjemplo,
  type SendTestFormState,
} from './templates-payload';
import { TEMPLATE_LIMITS, validateSendTestForm, valorFieldKey } from './templates-validation';
import { useSendTemplateTest } from './use-templates';

/**
 * Prueba de una plantilla (`POST .../:templateId/prueba`, solo ADMIN): **un envío REAL por WhatsApp**.
 *
 * Es la única forma de comprobar de punta a punta que Meta acepta la plantilla y que los parámetros
 * están bien antes de dejarla activa, así que el diálogo lo dice con todas las letras: manda un mensaje
 * de verdad al número que se escriba (y consume una conversación). El backend solo deja enviar
 * plantillas APROBADAS (400 con el estado actual si no lo está) y exige `headerImageUrl` cuando el
 * encabezado es de tipo IMAGEN.
 *
 * El resultado se queda a la vista (`summarizeSend`): a qué número salió, con qué id y si Meta avisó que
 * la ventana de 24 h estaba cerrada.
 */

export interface TemplateSendDialogProps {
  open: boolean;
  onClose: () => void;
  tenantId: string | null;
  /** Plantilla que se prueba (`null` = el diálogo no se pinta). */
  template: WhatsappTemplate | null;
  /** Catálogo de eventos: de ahí salen las etiquetas y los ejemplos de los campos. */
  catalogo: TemplateCatalog | null;
}

export function TemplateSendDialog({
  open,
  onClose,
  tenantId,
  template,
  catalogo,
}: TemplateSendDialogProps) {
  const sendTest = useSendTemplateTest(tenantId);

  const [form, setForm] = useState<SendTestFormState>(emptySendTestForm());
  const [errors, setErrors] = useState(EMPTY_FORM_ERRORS);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<SendTemplateResult | null>(null);

  const def =
    template && catalogo
      ? (catalogo.eventos.find((evento) => evento.evento === template.evento) ?? null)
      : null;

  /** Cada apertura arranca con los ejemplos del catálogo y sin resultado anterior. */
  useEffect(() => {
    if (!open || !template || !def) return;

    setForm(emptySendTestForm(valoresEjemplo(def.campos)));
    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);
    setResultado(null);
  }, [open, template, def]);

  const aprobada = template?.estado === 'APROBADA';
  /** Campos del catálogo que el mapeo de la plantilla usa (los que Meta recibe como parámetros). */
  const clavesUsadas = new Set((template?.parametros ?? []).map((parametro) => parametro.campo));
  const campos = def ? def.campos.filter((campo) => clavesUsadas.has(campo.clave)) : [];

  /** Cambia un valor sin tocar los demás. */
  function patchValor(clave: string, valor: string) {
    setForm((current) => ({ ...current, valores: { ...current.valores, [clave]: valor } }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!template) return;

    const nextErrors = validateSendTestForm(form, template.encabezadoTipo);
    if (Object.keys(nextErrors.fieldErrors).length > 0) {
      setErrors(nextErrors);
      setSubmitError(null);
      return;
    }

    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);

    try {
      const enviado = await sendTest.mutateAsync({
        templateId: template.id,
        payload: sendTestPayload(form),
      });
      setResultado(enviado);
    } catch (error) {
      // El backend explica el motivo exacto (no APROBADA, sin credenciales, Meta rechazó el envío…).
      setErrors(formErrorsFromApiError(error, SEND_TEST_FORM_FIELDS));
      setSubmitError(getErrorMessage(error));
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Enviar una prueba por WhatsApp"
      description={
        template
          ? `${describeIdentidad(template)} · ${describeEncabezado(template)}`
          : undefined
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={sendTest.isPending}>
            Cerrar
          </Button>
          <Button
            type="submit"
            form="template-send-form"
            loading={sendTest.isPending}
            disabled={!aprobada}
          >
            Enviar la prueba
          </Button>
        </>
      }
    >
      {!template || !def ? (
        <PageLoader label="Cargando la plantilla…" />
      ) : (
        <form id="template-send-form" onSubmit={handleSubmit} noValidate className="space-y-5">
          <FormErrorAlert message={submitError} errors={errors} />

          <Alert tone="warning">
            <p className="font-medium">Esto manda un mensaje real por WhatsApp.</p>
            <p className="mt-1">
              El destinatario lo recibe como cualquier mensaje del negocio (y solo se cobra si es una
              plantilla de marketing o va fuera de la ventana de 24 h).
              Úsalo con un número tuyo antes de dejar la plantilla activa.
            </p>
          </Alert>

          {!aprobada ? (
            <Alert>
              Meta todavía no aprobó esta plantilla (estado actual:{' '}
              <strong>{templateStatusMeta(template.estado).label}</strong>). Solo se puede probar una
              plantilla APROBADA: sincroniza su estado cuando Meta la revise.
            </Alert>
          ) : null}

          <Field
            label="Destinatario"
            htmlFor="sendTestTo"
            required
            hint="Número con código de país, sin `+` ni espacios (ej. 573001234567)."
            error={fieldError(errors, 'to')}
          >
            <Input
              id="sendTestTo"
              name="to"
              inputMode="tel"
              placeholder="573001234567"
              autoFocus
              maxLength={TEMPLATE_LIMITS.destinatarioMax}
              value={form.to}
              invalid={Boolean(fieldError(errors, 'to'))}
              onChange={(event) => setForm((current) => ({ ...current, to: event.target.value }))}
            />
          </Field>

          {template.encabezadoTipo === 'IMAGEN' ? (
            <Field
              label="URL de la imagen del encabezado"
              htmlFor="sendTestHeaderImageUrl"
              required
              hint="Meta exige una URL pública accesible: la imagen viaja como parámetro del encabezado."
              error={fieldError(errors, 'headerImageUrl')}
            >
              <Input
                id="sendTestHeaderImageUrl"
                name="headerImageUrl"
                placeholder="https://…/guia-de-envio.jpg"
                value={form.headerImageUrl}
                invalid={Boolean(fieldError(errors, 'headerImageUrl'))}
                onChange={(event) =>
                  setForm((current) => ({ ...current, headerImageUrl: event.target.value }))
                }
              />
            </Field>
          ) : null}

          <div className="space-y-4">
            <p className="text-caption text-ink-muted">
              Valores de las {variablesDelCuerpo(template.cuerpo).length} variables del cuerpo: los que
              dejes vacíos se reemplazan con el ejemplo del catálogo (Meta rechaza parámetros vacíos).
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              {campos.map((campo) => (
                <Field
                  key={campo.clave}
                  label={campoEtiqueta(def.campos, campo.clave)}
                  htmlFor={`sendTest-${campo.clave}`}
                  error={fieldError(errors, valorFieldKey(campo.clave))}
                >
                  <Input
                    id={`sendTest-${campo.clave}`}
                    name={valorFieldKey(campo.clave)}
                    maxLength={TEMPLATE_LIMITS.valor}
                    placeholder={campo.ejemplo}
                    value={form.valores[campo.clave] ?? ''}
                    invalid={Boolean(fieldError(errors, valorFieldKey(campo.clave)))}
                    onChange={(event) => patchValor(campo.clave, event.target.value)}
                  />
                </Field>
              ))}
            </div>
          </div>

          <Field
            label="Id de la conversación (opcional)"
            htmlFor="sendTestConversationId"
            hint="Si lo indicas, el mensaje queda en el historial de esa conversación del inbox."
            error={fieldError(errors, 'conversationId')}
          >
            <Input
              id="sendTestConversationId"
              name="conversationId"
              placeholder="Ej. 3f4c1d2e-9a77-4e1b-8c3d-1b2c3d4e5f60"
              value={form.conversationId}
              invalid={Boolean(fieldError(errors, 'conversationId'))}
              onChange={(event) =>
                setForm((current) => ({ ...current, conversationId: event.target.value }))
              }
            />
          </Field>

          {resultado ? (
            <Alert tone={resultado.enviado ? 'success' : 'danger'}>
              <p className="font-medium">{summarizeSend(resultado, form.to)}</p>
              {resultado.contenido ? (
                <p className="mt-1 whitespace-pre-wrap">Texto enviado: {resultado.contenido}</p>
              ) : null}
            </Alert>
          ) : null}

        </form>
      )}
    </Modal>
  );
}

